import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card } from '@repo/videa-components/src/index';
import classNames from 'classnames';

import { getVariantStyles } from './helpers';

export type InfoBannerVariant =
  | 'default'
  | 'primary'
  | 'warning'
  | 'error'
  | 'success'
  | 'progress';

interface InfoBannerProps {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  variant?: InfoBannerVariant;
  /**
   * Icons and colors are set by the variant, but each can be overridden with the following props.
   */
  icon?: React.ReactNode;
  iconColor?: string;
  titleColor?: string;
  backgroundColor?: string;
  actionButton?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  hideIcon?: boolean;
  onClose?: () => void;
}

/**
 * InfoBanner component with customizable icons and colors
 */
export const InfoBanner = ({
  title,
  description,
  variant = 'default',
  icon,
  iconColor,
  titleColor,
  backgroundColor,
  actionButton,
  footer,
  className,
  hideIcon = false,
  onClose,
}: InfoBannerProps) => {
  const variantStyles = getVariantStyles(variant);

  // Determines whether to use the provided icon or the default icon based on the variant
  const finalIcon = hideIcon
    ? null
    : icon || <FontAwesomeIcon icon={variantStyles.icon} />;
  const finalIconColor = iconColor || variantStyles.iconClass;
  const finalTitleColor = titleColor || variantStyles.titleClass;
  const finalBackgroundColor = backgroundColor || variantStyles.cardClass;

  return (
    <Card
      className={classNames(
        finalBackgroundColor,
        'vai-p-5 vai-relative vai-border vai-border-solid',
        className,
      )}
    >
      {onClose && (
        <Button
          variant="ghost"
          size="xs"
          className={classNames(
            'vai-absolute vai-top-2 vai-right-2 !vai-p-0 !vai-w-5 !vai-h-5',
            variantStyles.closeButtonClass,
          )}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      )}

      <div className="vai-flex vai-justify-between vai-w-full vai-pr-8 vai-gap-4">
        <div className="vai-flex vai-flex-col vai-gap-1 vai-flex-1">
          <div className="vai-flex vai-items-center vai-gap-3">
            {finalIcon && (
              <div
                className={classNames(
                  'vai-flex vai-items-center vai-justify-center vai-w-4 vai-h-4',
                  finalIconColor,
                  variant === 'progress' && 'vai-animate-spin',
                )}
              >
                {finalIcon}
              </div>
            )}
            <h3
              className={classNames(
                'vai-font-medium vai-text-sm',
                finalTitleColor,
              )}
            >
              {title}
            </h3>
          </div>
          <div className="vai-text-sm vai-ml-7">{description}</div>
        </div>
        {actionButton && (
          <div className="vai-flex vai-items-center vai-gap-2 vai-shrink-0">
            {actionButton}
          </div>
        )}
      </div>
      {footer && <div className="vai-mt-2 vai-ml-7">{footer}</div>}
    </Card>
  );
};
